// src/components/GroupIntake/validationSchemas.js
import * as Yup from 'yup';

// Schema for contact person
export const contactPersonSchema = Yup.object().shape({
  name: Yup.string(),
  email: Yup.string().email('Invalid email'),
  phone: Yup.string()
});

// Schema for facility details
export const facilityDetailsSchema = Yup.object().shape({
  facilityType: Yup.string().required('Facility type is required'),
  traumaLevel: Yup.string(),
  teamModel: Yup.string()
});

// Schema for clinical expectations
export const clinicalExpectationsSchema = Yup.object().shape({
  requiredCaseTypes: Yup.array().of(Yup.string()),
  emrSystem: Yup.string()
});

// Schema for assignment
export const assignmentSchema = Yup.object().shape({
  startDate: Yup.string(),
  type: Yup.array().of(Yup.string())
});

// Schema for clinical requirements
export const clinicalRequirementsSchema = Yup.object().shape({
  requiredCaseTypes: Yup.array().of(Yup.string())
});

// Schema for listings
export const listingSchema = Yup.object().shape({
  payAmount: Yup.number().required('Pay amount is required'),
  payPeriod: Yup.string().required('Pay period is required'),
  contractType: Yup.string().required('Contract type is required'),
  assignmentType: Yup.array().of(Yup.string()),
  assignment: assignmentSchema,
  scheduleType: Yup.string(),
  estimatedWeeklyHours: Yup.number(),
  clinicalExpectations: clinicalExpectationsSchema,
  clinicalRequirements: clinicalRequirementsSchema,
  includesHousing: Yup.boolean(),
  includesTravel: Yup.boolean(),
  malpracticeCovered: Yup.boolean()
});

// Schema for facility
export const facilitySchema = Yup.object().shape({
  name: Yup.string().required('Facility name is required'),
  location: Yup.string().required('Location is required'),
  contactPerson: contactPersonSchema,
  facilityDetails: facilityDetailsSchema.required('Facility details are required'),
  clinicalExpectations: clinicalExpectationsSchema,
  listings: Yup.array().of(listingSchema).min(1, 'At least one listing is required')
});

// Schema for group
export const groupSchema = Yup.object().shape({
  name: Yup.string().required('Group name is required'),
  description: Yup.string(),
  logoUrl: Yup.string().url('Must be a valid URL'),
  website: Yup.string().url('Must be a valid URL'),
  contact: contactPersonSchema
});

// Schema for complete form
export const formSchema = Yup.object().shape({
  group: groupSchema.required('Group information is required'),
  facilities: Yup.array().of(facilitySchema).min(1, 'At least one facility is required')
});

// Helper function to validate autosave data
export const validateAutosaveData = async (section, data) => {
  try {
    let result = { isValid: false, errors: {} };
    
    if (section === 'group') {
      await groupSchema.validate(data, { abortEarly: false });
      result.isValid = true;
    } 
    else if (section === 'facilities') {
      // For facilities, we validate each facility individually
      const validationPromises = data.map((facility, index) => 
        facilitySchema.validate(facility, { abortEarly: false })
          .catch(err => ({ index, errors: err.errors }))
      );
      
      const results = await Promise.all(validationPromises);
      const errors = results.filter(r => r && r.errors);
      
      if (errors.length === 0) {
        result.isValid = true;
      } else {
        result.isValid = false;
        result.errors = errors.reduce((acc, curr) => {
          acc[`facility_${curr.index}`] = curr.errors;
          return acc;
        }, {});
      }
    }
    
    return result;
  } catch (err) {
    return {
      isValid: false,
      errors: err.inner ? err.inner.reduce((acc, err) => {
        acc[err.path] = err.message;
        return acc;
      }, {}) : { general: err.message || 'Validation failed' }
    };
  }
};

// Create a valid empty facility structure with all required fields filled
export const createEmptyFacility = () => ({
  name: '',
  location: '',
  contactPerson: { name: '', email: '', phone: '' },
  facilityDetails: { 
    facilityType: 'Hospital', // Set a default value for required field
    traumaLevel: '',
    teamModel: '' 
  },
  clinicalExpectations: { requiredCaseTypes: [], emrSystem: '' },
  listings: [{
    payAmount: 80, // Default value
    payPeriod: 'hourly', // Default value
    contractType: 'W2', // Default value
    assignmentType: [],
    assignment: { startDate: '', type: [] },
    scheduleType: '',
    estimatedWeeklyHours: 0,
    clinicalExpectations: { requiredCaseTypes: [], emrSystem: '' },
    includesHousing: false,
    includesTravel: false,
    malpracticeCovered: false
  }]
});
